import React, { useState, useEffect } from 'react';
import { db } from '../firebase';
import { collection, query, where, getDocs, orderBy, Timestamp } from 'firebase/firestore';
import { useRestaurant } from '../context/RestaurantContext';
import OrderCard from './OrderCard';
import DatePicker from 'react-datepicker';
import "react-datepicker/dist/react-datepicker.css";
import { format, startOfMonth, endOfMonth } from 'date-fns';
import { es } from 'date-fns/locale';
import Papa from 'papaparse';
import './History.css';

const History = () => {
  const { selectedRestaurant } = useRestaurant();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedDate, setSelectedDate] = useState(new Date());

  useEffect(() => {
    if (selectedRestaurant) {
      fetchOrdersByDate(selectedDate);
    }
  }, [selectedDate, selectedRestaurant]);

  const fetchOrdersByDate = async (date) => {
    if (!selectedRestaurant) return;
    setLoading(true);
    setOrders([]);
    const startOfDay = new Date(new Date(date).setHours(0, 0, 0, 0));
    const endOfDay = new Date(new Date(date).setHours(23, 59, 59, 999));
    
    const q = query(
      collection(db, "orders"),
      where("restaurantId", "==", selectedRestaurant),
      where("timestamp", ">=", Timestamp.fromDate(startOfDay)),
      where("timestamp", "<=", Timestamp.fromDate(endOfDay)),
      orderBy("timestamp", "desc")
    );

    try {
      const querySnapshot = await getDocs(q);
      setOrders(querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    } catch (error) {
      console.error("Error al obtener historial: ", error);
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadCSV = async () => {
    if (!selectedRestaurant) return alert("Por favor, selecciona un restaurante.");
    alert("Generando reporte del mes para el restaurante actual...");
    
    const start = startOfMonth(selectedDate);
    const end = endOfMonth(selectedDate);

    const q = query(
        collection(db, "orders"),
        where("restaurantId", "==", selectedRestaurant),
        where("timestamp", ">=", Timestamp.fromDate(start)),
        where("timestamp", "<=", Timestamp.fromDate(end)),
        orderBy("timestamp", "asc")
    );
    
    await generateAndDownloadCsv(q, `Reporte_${selectedRestaurant}_${format(selectedDate, 'MMMM-yyyy', { locale: es })}.csv`);
  };

  // --- INICIO DE LA NUEVA FUNCIONALIDAD ---
  const handleDownloadLegacyCSV = async () => {
    alert("Generando reporte de órdenes antiguas (sin ID de restaurante)...");

    const start = startOfMonth(selectedDate);
    const end = endOfMonth(selectedDate);

    // Consulta especial que NO filtra por restaurantId
    const q = query(
        collection(db, "orders"),
        where("timestamp", ">=", Timestamp.fromDate(start)),
        where("timestamp", "<=", Timestamp.fromDate(end)),
        orderBy("timestamp", "asc")
    );
    
    // Filtro adicional para asegurarnos de que solo traiga las que no tienen el campo
    const querySnapshot = await getDocs(q);
    const legacyOrders = querySnapshot.docs
        .map(doc => ({ id: doc.id, ...doc.data() }))
        .filter(order => !order.restaurantId);

    if (legacyOrders.length === 0) {
        alert("No se encontraron órdenes antiguas en el mes seleccionado.");
        return;
    }

    await generateAndDownloadCsv(legacyOrders, `Reporte_Antiguo_${format(selectedDate, 'MMMM-yyyy', { locale: es })}.csv`, true);
  };
  // --- FIN DE LA NUEVA FUNCIONALIDAD ---

  // Función genérica para generar y descargar el CSV
  const generateAndDownloadCsv = async (queryOrData, fileName, isDataArray = false) => {
    try {
      let monthlyOrders = [];
      if (isDataArray) {
        monthlyOrders = queryOrData;
      } else {
        const querySnapshot = await getDocs(queryOrData);
        monthlyOrders = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      }

      if (monthlyOrders.length === 0) {
        alert("No se encontraron órdenes para este reporte.");
        return;
      }

      const flattenedData = [];
      monthlyOrders.forEach(order => {
        const orderTotal = order.total ? Number(order.total).toFixed(2) : '0.00';
        if (order.items && order.items.length > 0) {
          order.items.forEach(item => {
            flattenedData.push({
              "ID Orden": order.id, "Fecha": order.timestamp ? format(order.timestamp.toDate(), 'yyyy-MM-dd') : '',
              "Hora": order.hora, "Cliente": order.customer_name, "Total de la Orden": orderTotal,
              "Tipo": order.delivery_type, "Dirección": order.address, "Estado": order.status,
              "Cantidad": item.quantity, "Producto": item.name, "Notas": item.notes
            });
          });
        }
      });

      const csv = Papa.unparse(flattenedData);
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error("Error al generar el CSV: ", error);
      alert("Hubo un error al generar el reporte.");
    }
  };


  const handleStatusChange = () => { /* ... */ };
  const formattedDate = format(selectedDate, "eeee, d 'de' MMMM 'de' yyyy", { locale: es });

  return (
    <div className="history-container">
      <div className="history-header">
        <h2>Historial de Órdenes</h2>
        <div className="controls-container">
            <div className="datepicker-container">
                <span>Seleccionar fecha: </span>
                <DatePicker selected={selectedDate} onChange={(date) => setSelectedDate(date)} dateFormat="dd/MM/yyyy" locale={es} />
            </div>
            <button onClick={handleDownloadCSV} className="download-btn">Descargar CSV del Mes</button>
            {/* --- INICIO DE LA NUEVA FUNCIONALIDAD --- */}
            <button onClick={handleDownloadLegacyCSV} className="download-btn legacy">Descargar Órdenes Antiguas</button>
            {/* --- FIN DE LA NUEVA FUNCIONALIDAD --- */}
        </div>
      </div>
      
      <h3 className="date-title">{formattedDate}</h3>
      
      {loading ? ( <p>Buscando órdenes...</p> ) : (
        <div className="orders-grid">
          {orders.length > 0 ? (
            orders.map(order => ( <OrderCard key={order.id} order={order} onStatusChange={handleStatusChange} /> ))
          ) : (
            <p>No se encontraron órdenes para esta fecha.</p>
          )}
        </div>
      )}
    </div>
  );
};

export default History;